import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { 
  TutorialContextValue, 
  TutorialState, 
  TutorialModule,
  TutorialStep,
  TutorialProgress as TutorialProgressType
} from '@/app/types/tutorial';
import { getTutorialModules } from '@/app/utils/tutorialStepsSimplified';
import { tutorialSound } from '@/app/utils/tutorialSounds';

const TUTORIAL_STORAGE_KEY = 'isocad_tutorial_state';

// Event-System für Tutorial-Interaktionen
export type TutorialEvent = 
  | { type: 'tool-selected', tool: string }
  | { type: 'element-created', elementType: string }
  | { type: 'element-selected', count: number }
  | { type: 'dialog-opened', dialog: string }
  | { type: 'menu-opened', menu: string }
  | { type: 'action-performed', action: string };

// Global event bus für Tutorial-Events
export const tutorialEventListeners = new Set<(event: TutorialEvent) => void>();

export function emitTutorialEvent(event: TutorialEvent) {
  tutorialEventListeners.forEach(listener => listener(event));
}

const defaultProgress: TutorialProgressType = {
  currentModule: null,
  currentStepIndex: 0,
  completedModules: [],
  skippedModules: [],
};

const defaultState: TutorialState = {
  isActive: false,
  progress: defaultProgress,
  hasSeenWelcome: false,
  neverShowAgain: false,
  undoStack: [],
  soundEnabled: true,
};

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);

export function TutorialProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TutorialState>(() => {
    // Load from localStorage
    try {
      const saved = localStorage.getItem(TUTORIAL_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('[TutorialProvider] Failed to load tutorial state:', e);
    }
    return defaultState;
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save tutorial state:', e);
    }
  }, [state]);

  const getCurrentStep = useCallback((): TutorialStep | null => {
    if (!state.isActive || !state.progress.currentModule) {
      return null;
    }

    const modules = getTutorialModules();
    const currentModule = modules.find(m => m.id === state.progress.currentModule);
    
    if (!currentModule) {
      return null;
    }

    const step = currentModule.steps[state.progress.currentStepIndex];
    return step || null;
  }, [state.isActive, state.progress.currentModule, state.progress.currentStepIndex]);

  const startTutorial = useCallback((module: TutorialModule) => {
    setState(prev => ({
      ...prev,
      isActive: true,
      hasSeenWelcome: true,
      progress: {
        ...prev.progress,
        currentModule: module,
        currentStepIndex: 0,
      },
    }));
  }, []);

  const stopTutorial = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: false,
      progress: {
        ...prev.progress,
        currentModule: null,
        currentStepIndex: 0,
      },
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      if (!prev.progress.currentModule) return prev;

      const modules = getTutorialModules();
      const currentModule = modules.find(m => m.id === prev.progress.currentModule);
      
      if (!currentModule) return prev;

      const nextIndex = prev.progress.currentStepIndex + 1;

      // Check if we've reached the end
      if (nextIndex >= currentModule.steps.length) {
        // Mark module as complete
        return {
          ...prev,
          isActive: false,
          progress: {
            ...prev.progress,
            currentModule: null,
            currentStepIndex: 0,
            completedModules: prev.progress.completedModules.includes(prev.progress.currentModule)
              ? prev.progress.completedModules
              : [...prev.progress.completedModules, prev.progress.currentModule],
            lastCompletedAt: new Date().toISOString(),
          },
        };
      }

      // Move to next step
      return {
        ...prev,
        progress: {
          ...prev.progress,
          currentStepIndex: nextIndex,
        },
      };
    });
  }, []);
  
  // Event-Handler entfernt - wird jetzt in TutorialOverlay behandelt
  // um doppeltes Weitergehen zu vermeiden
  
  // Event-Listener registrieren - DEAKTIVIERT
  // Die Event-Behandlung erfolgt jetzt ausschließlich in TutorialOverlay
  // um besseres UX-Feedback zu ermöglichen (conditionMet Animation)
  /*
  useEffect(() => {
    if (!state.isActive) return;
    
    tutorialEventListeners.add(handleTutorialEvent);
    return () => {
      tutorialEventListeners.delete(handleTutorialEvent);
    };
  }, [state.isActive, handleTutorialEvent]);
  */

  const previousStep = useCallback(() => {
    setState(prev => {
      if (prev.progress.currentStepIndex === 0) return prev;

      return {
        ...prev,
        progress: {
          ...prev.progress,
          currentStepIndex: prev.progress.currentStepIndex - 1,
        },
      };
    });
  }, []);

  const skipStep = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const skipTutorial = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: false,
      progress: {
        ...prev.progress,
        skippedModules: prev.progress.currentModule && 
                       !prev.progress.skippedModules.includes(prev.progress.currentModule)
          ? [...prev.progress.skippedModules, prev.progress.currentModule]
          : prev.progress.skippedModules,
        currentModule: null,
        currentStepIndex: 0,
      },
    }));
  }, []);

  const resetTutorial = useCallback((module?: TutorialModule) => {
    setState(prev => {
      if (module) {
        // Reset specific module
        return {
          ...prev,
          progress: {
            ...prev.progress,
            completedModules: prev.progress.completedModules.filter(m => m !== module),
            skippedModules: prev.progress.skippedModules.filter(m => m !== module),
          },
        };
      } else {
        // Reset all
        return {
          ...prev,
          progress: defaultProgress,
          hasSeenWelcome: false,
        };
      }
    });
  }, []);

  const markModuleComplete = useCallback((module: TutorialModule) => {
    setState(prev => ({
      ...prev,
      progress: {
        ...prev.progress,
        completedModules: prev.progress.completedModules.includes(module)
          ? prev.progress.completedModules
          : [...prev.progress.completedModules, module],
        lastCompletedAt: new Date().toISOString(),
      },
    }));
  }, []);

  const setNeverShowAgain = useCallback((value: boolean) => {
    setState(prev => ({
      ...prev,
      neverShowAgain: value,
    }));
  }, []);

  const isStepComplete = useCallback((stepId: string): boolean => {
    // Check if a specific step has been completed
    // This can be extended based on actual completion logic
    return false;
  }, []);

  const getTutorialForModule = useCallback((module: TutorialModule) => {
    const modules = getTutorialModules();
    return modules.find(m => m.id === module);
  }, []);

  // Neu: Undo Tutorial Step
  const undoTutorialStep = useCallback(() => {
    // Gehe einfach einen Schritt zurück
    previousStep();
    if (state.soundEnabled) {
      tutorialSound.playUndo();
    }
  }, [previousStep, state.soundEnabled]);

  // Neu: Tutorial pausieren
  const pauseTutorial = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: false,
      progress: {
        ...prev.progress,
        canResume: true,
        pausedAt: new Date().toISOString(),
      },
    }));
  }, []);

  // Neu: Tutorial fortsetzen
  const resumeTutorial = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: true,
      progress: {
        ...prev.progress,
        canResume: false,
        pausedAt: undefined,
      },
    }));
    if (state.soundEnabled) {
      tutorialSound.playStart();
    }
  }, [state.soundEnabled]);

  // Neu: Sound togglen
  const toggleSound = useCallback(() => {
    setState(prev => {
      const newEnabled = !prev.soundEnabled;
      tutorialSound.setEnabled(newEnabled);
      return {
        ...prev,
        soundEnabled: newEnabled,
      };
    });
  }, []);

  // Memoize currentStep to avoid unnecessary recalculations
  const currentStep = useMemo(() => getCurrentStep(), [getCurrentStep]);

  // Sound bei Step-Wechsel
  useEffect(() => {
    if (state.isActive && state.soundEnabled && currentStep) {
      tutorialSound.playClick();
    }
  }, [state.progress.currentStepIndex, state.isActive, state.soundEnabled, currentStep]);

  const value: TutorialContextValue = useMemo(() => ({
    state,
    currentStep,
    startTutorial,
    stopTutorial,
    nextStep,
    previousStep,
    skipStep,
    skipTutorial,
    resetTutorial,
    markModuleComplete,
    setNeverShowAgain,
    isStepComplete,
    getTutorialForModule,
    undoTutorialStep,
    pauseTutorial,
    resumeTutorial,
    toggleSound,
  }), [
    state,
    currentStep,
    startTutorial,
    stopTutorial,
    nextStep,
    previousStep,
    skipStep,
    skipTutorial,
    resetTutorial,
    markModuleComplete,
    setNeverShowAgain,
    isStepComplete,
    getTutorialForModule,
    undoTutorialStep,
    pauseTutorial,
    resumeTutorial,
    toggleSound,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
}

// Safe version that returns undefined instead of throwing
export function useTutorialSafe() {
  const context = useContext(TutorialContext);
  return context;
}